import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Create,
    GetAllDivisions,
    GetAllPositions,
    GetAllSupervisors
} from "adminApi/EmployeeApi/Employee";
import Spinner from "Common/Globle/Spinner";
import AddEmployeeForm from "./components/AddEmployeeForm";
import fields from "../json/Employee.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { EmployeeRules } from "validation/ValidationRules";
import { validateArrayForm } from "utils/validation";
import useFetchAllData from "Hooks/useFetchAllData";

const AddEmployee = () => {
    const navigate = useNavigate();

    /**
     * Helper function to initialize form values from the fields array.
     */
    const buildInitialObject = (fieldList = []) =>
        fieldList.reduce((acc, field) => {
            acc[field.name] = field.name === "enable_login" ? "true" : "";
            return acc;
        }, {});

    /**
     * Initialize form values with default values for all sections.
     */
    const initialValues = {
        ...buildInitialObject(fields.basic),
        ...buildInitialObject(fields.position),
        ...buildInitialObject(fields.benefits),
        ...buildInitialObject(fields.class),
        ...buildInitialObject(fields.supervisor),
        ...buildInitialObject(fields.biographical),
        ...buildInitialObject(fields.contacts),
        ...buildInitialObject(fields.emergency),
        custom_fields: []
    };

    /**
     * useFormChange hook to handle form changes.
     */
    const {
        values,
        errors,
        handleChange,
        handleFileChange,
        handleArrayChange,
        setValues,
        setErrors,
    } = useFormChange(initialValues, EmployeeRules);


    // Fetch Candidates
    const {
        data: DivisionOptions = []
    } = useFetchAllData(
        GetAllDivisions
    );

    const {
        data: PositionOptions = []
    } = useFetchAllData(
        GetAllPositions
    );

    const {
        data: SupervisorOptions = []
    } = useFetchAllData(
        GetAllSupervisors
    );


    /**
     * useFormSubmit hook to handle form submission.
     */
    const { loading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            if (formValues.enable_login === "false") {
                if (formValues.username) {
                    delete formValues.username;
                }
                if (formValues.password) {
                    delete formValues.password;
                }
            }
            const formData = new FormData();
            Object.entries(formValues).forEach(([k, v]) => {
                if (Array.isArray(v)) {
                    formData.append(k, JSON.stringify(v));
                } else {
                    formData.append(k, v ?? "");
                }
            });
            return await Create({ data: formData });
        },
        (vals) => {
            const err = validateArrayForm(vals, EmployeeRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Employee created successfully"
    );

    return (
        <>
            {loading && <Spinner isSpinner />}
            <AddEmployeeForm
                fields={fields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleArrayChange={handleArrayChange}
                setValues={setValues}
                setErrors={setErrors}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                title="Create Employee"
                rules={EmployeeRules}
                validateArrayForm={validateArrayForm}
                buildInitialObject={buildInitialObject}
                imagePreview={values.photograph_urlPreview || values.photograph_url}
                DivisionOptions={DivisionOptions}
                PositionOptions={PositionOptions}
                SupervisorOptions={SupervisorOptions}
            />
        </>
    );
};

export default AddEmployee;
